import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useFetch } from "../lib/hooks";
import { PageTitle, Panel, Field, inputCls, btnPrimary, btnGhost, btnAccent } from "./ui";
import { BrandLoader } from "../components/site/Logo";

const EMPTY = { hero_id: "", secondary_ids: ["", ""], important_ids: ["", "", "", ""], pinned: [] };

const Pick = ({ value, onChange, items, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId}>
    <option value="">— automatski (najnovije) —</option>
    {items.map((a) => <option key={a.id} value={a.id}>{a.headline}</option>)}
  </select>
);

const Preview = ({ p }) => (
  <ol className="space-y-1 text-sm" data-testid="homepage-preview">
    <li><strong>Glavna:</strong> {p.featured?.headline || "—"}</li>
    {p.secondary.map((a) => <li key={a.id}><strong>Sporedna:</strong> {a.headline}</li>)}
    {p.latest.filter((a) => a.pinned).map((a) => <li key={a.id}><strong>Prikvačeno:</strong> {a.headline}</li>)}
    {p.important.map((a) => <li key={a.id}><strong>Važno za vas:</strong> {a.headline}</li>)}
  </ol>
);

export default function Homepage() {
  const { data: cfg, loading } = useFetch("/admin/homepage");
  const { data: arts } = useFetch("/admin/homepage/candidates");
  const [f, setF] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (cfg?.draft?.updated_at) {
      const d = cfg.draft;
      setF({ hero_id: d.hero_id || "", secondary_ids: [...d.secondary_ids, "", ""].slice(0, 2), important_ids: [...d.important_ids, "", "", "", ""].slice(0, 4), pinned: d.pinned || [] });
    }
  }, [cfg]);
  if (loading || !arts) return <BrandLoader />;
  const items = arts;
  const payload = { ...f, secondary_ids: f.secondary_ids.filter(Boolean), important_ids: f.important_ids.filter(Boolean), pinned: f.pinned.filter((p) => p.article_id) };
  const saveDraft = async () => {
    try {
      await api.put("/admin/homepage", payload);
      const { data } = await api.get("/admin/homepage/preview");
      setPreview(data);
      toast.success("Nacrt naslovnice sačuvan");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const publish = async () => {
    if (!(await saveDraft())) return;
    try { await api.post("/admin/homepage/publish"); toast.success("Naslovnica objavljena"); } catch (e) { toast.error(errMsg(e)); }
  };
  const setArr = (k, i) => (v) => setF({ ...f, [k]: f[k].map((x, j) => (j === i ? v : x)) });
  return (
    <div data-testid="admin-homepage-page">
      <PageTitle title="Naslovnica" sub="Ručni izbor članaka. Prazna mjesta automatski popunjavaju najnoviji objavljeni tekstovi; neobjavljeni se nikad ne prikazuju."
        actions={<><button onClick={saveDraft} className={btnGhost} data-testid="homepage-save-draft">Sačuvaj i pregledaj</button><button onClick={publish} className={btnAccent} data-testid="homepage-publish">Objavi naslovnicu</button></>} />
      {cfg?.published?.published_at && <p className="mb-4 text-xs text-muted-foreground" data-testid="homepage-published-info">Zadnja objava: {cfg.published.published_by} · {fmtDateTime(cfg.published.published_at)}</p>}
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Glavne pozicije">
          <div className="space-y-3">
            <Field label="Glavni članak"><Pick value={f.hero_id} onChange={(v) => setF({ ...f, hero_id: v })} items={items} testId="homepage-hero" /></Field>
            {f.secondary_ids.map((v, i) => <Field key={i} label={`Sporedni ${i + 1}`}><Pick value={v} onChange={setArr("secondary_ids", i)} items={items} testId={`homepage-secondary-${i}`} /></Field>)}
          </div>
        </Panel>
        <Panel title="Važno za vas">
          <div className="space-y-3">{f.important_ids.map((v, i) => <Pick key={i} value={v} onChange={setArr("important_ids", i)} items={items} testId={`homepage-important-${i}`} />)}</div>
        </Panel>
        <Panel title="Prikvačeno u Najnovije (s rokom)">
          <div className="space-y-2">
            {f.pinned.map((p, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[1fr_200px_auto]">
                <Pick value={p.article_id} onChange={(v) => setArr("pinned", i)({ ...p, article_id: v })} items={items} testId={`homepage-pin-${i}`} />
                <input type="date" value={(p.until || "").slice(0, 10)} onChange={(e) => setArr("pinned", i)({ ...p, until: e.target.value ? `${e.target.value}T22:00:00+00:00` : "" })} className={inputCls} aria-label="Prikvačeno do" data-testid={`homepage-pin-until-${i}`} />
                <button onClick={() => setF({ ...f, pinned: f.pinned.filter((_, j) => j !== i) })} className="text-xs text-destructive">Ukloni</button>
              </div>
            ))}
            {f.pinned.length < 5 && <button onClick={() => setF({ ...f, pinned: [...f.pinned, { article_id: "", until: "" }] })} className="text-xs font-semibold text-pulse hover:underline" data-testid="homepage-add-pin">+ Prikvači članak</button>}
          </div>
        </Panel>
        <Panel title="Pregled (nacrt)">{preview ? <Preview p={preview} /> : <p className="text-sm text-muted-foreground">Kliknite „Sačuvaj i pregledaj“.</p>}</Panel>
      </div>
    </div>
  );
}
